import type { Progress } from '../shared/progress'

export interface Account {
  id: string
  kind: 'guest' | 'max' | 'phone'
  firstName: string
  lastName: string
  age: number | null
  avatarUrl: string | null
  /** Короткий код, по которому добавляют в друзья. Его дети диктуют вслух. */
  inviteCode: string
  /** Секретный ключ: по нему прогресс возвращается на новом устройстве. */
  restoreCode: string
  hasPhone: boolean
  sound: boolean
  /** Имя бота MAX для ссылки-приглашения; пустая строка — бот не настроен. */
  botName: string
  /** Настроен ли вход через MAX. Пока бота нет — кнопку входа не показываем. */
  maxEnabled: boolean
}

/** Чей это ключ — показываем до того, как переключить сессию. */
export interface RestorePeek {
  name: string
  pct: number
}

export interface FriendRow {
  id: string
  name: string
  avatarUrl: string | null
  pct: number
  streak: number
  stickers: number
  lastPlayed: string | null
  isMe: boolean
}

export interface FeedRow {
  id: string
  playerId: string
  name: string
  avatarUrl: string | null
  text: string
  createdAt: string
}

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const isFormData = init?.body instanceof FormData
  const response = await fetch(url, {
    ...init,
    headers: {
      ...(init?.body && !isFormData ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  })

  if (!response.ok) {
    let message = 'Ошибка запроса'
    try {
      const body = (await response.json()) as { error?: string }
      message = body.error || message
    } catch {
      message = response.statusText || message
    }
    throw new Error(message)
  }

  return response.status === 204 ? (undefined as T) : ((await response.json()) as T)
}

export const api = {
  me: () => request<{ player: Account; progress: Progress }>('/api/me'),

  loginMax: (initData: string) =>
    request<{ player: Account; progress: Progress }>('/api/auth/max', {
      method: 'POST',
      body: JSON.stringify({ initData }),
    }),

  restorePeek: (code: string) =>
    request<RestorePeek>('/api/restore/peek', { method: 'POST', body: JSON.stringify({ code }) }),

  restore: (code: string) =>
    request<{ player: Account; progress: Progress }>('/api/restore', {
      method: 'POST',
      body: JSON.stringify({ code }),
    }),

  updateMe: (patch: Partial<Pick<Account, 'firstName' | 'lastName' | 'age'>>) =>
    request<{ player: Account }>('/api/me', { method: 'PATCH', body: JSON.stringify(patch) }),

  putProgress: (progress: Progress) =>
    request<{ progress: Progress }>('/api/me/progress', { method: 'PUT', body: JSON.stringify({ progress }) }),

  setPhone: (payload: { phone: string; authDate: string; hash: string }) =>
    request<{ player: Account }>('/api/me/phone', { method: 'POST', body: JSON.stringify(payload) }),

  uploadAvatar: (blob: Blob) => {
    const form = new FormData()
    form.append('file', blob, 'avatar.jpg')
    return request<{ player: Account }>('/api/me/avatar', { method: 'POST', body: form })
  },

  deleteAvatar: () => request<{ player: Account }>('/api/me/avatar', { method: 'DELETE' }),

  friends: () => request<{ friends: FriendRow[]; inviteCode: string }>('/api/friends'),

  addFriend: (payload: { code?: string; phone?: string }) =>
    request<{ friend: FriendRow }>('/api/friends', { method: 'POST', body: JSON.stringify(payload) }),

  removeFriend: (id: string) => request<void>(`/api/friends/${id}`, { method: 'DELETE' }),

  feed: () => request<{ feed: FeedRow[] }>('/api/feed'),
}

export function errText(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback
}
